import axiosApiInstance from '../../../api/axiosClient';

export type ReleaseInfoForTrackAutofill = {
    name?: string;
    type?: string;
    artists?: string[];
};

type ReleaseInfoSource = ReleaseInfoForTrackAutofill & {
    main_artists?: Array<string | API.MusicDistribution.Artist>;
    artists?: string[] | API.MusicDistribution.Artist[];
};

function normalizeArtistKeys(
    artists?: string[] | API.MusicDistribution.Artist[],
    main_artists?: Array<string | API.MusicDistribution.Artist>,
): string[] {
    if (artists?.length) {
        return artists
            .map((artist) => (typeof artist === 'string' ? artist : artist.key_name))
            .filter((key): key is string => typeof key === 'string' && key.trim() !== '');
    }
    if (main_artists?.length) {
        return main_artists
            .map((artist) => (typeof artist === 'string' ? artist : artist.key_name))
            .filter((key): key is string => typeof key === 'string' && key.trim() !== '');
    }
    return [];
}

/** Merge form + atom release info; atom is fallback when release-info fields are unmounted. */
export function resolveReleaseInfoForTrackAutofill(
    formValues: ReleaseInfoSource = {},
    atomValues: ReleaseInfoSource = {},
): ReleaseInfoForTrackAutofill {
    const merged = {...atomValues, ...formValues};
    return {
        name: merged.name || '',
        type: merged.type || '',
        artists: normalizeArtistKeys(merged.artists, merged.main_artists),
    };
}

export async function fetchReleaseInfoForTrackAutofill(
    releaseId: string,
): Promise<ReleaseInfoForTrackAutofill> {
    const response = await axiosApiInstance.get(`/api/products/releases/${releaseId}`, {
        params: {without_tracks: true},
    });
    const data = response.data as API.MusicDistribution.MusicRelease;
    return {
        name: data.name,
        type: data.type,
        artists: data.artists?.map((artist) => artist.key_name) || [],
    };
}

export async function resolveReleaseInfoForTrackAutofillAsync(
    formValues: ReleaseInfoSource,
    atomValues: ReleaseInfoSource,
    releaseId?: string,
): Promise<ReleaseInfoForTrackAutofill> {
    let resolved = resolveReleaseInfoForTrackAutofill(formValues, atomValues);
    const needsName =
        shouldAutofillTrackTitleFromRelease(resolved.type) && !resolved.name?.trim();
    const needsArtists = !resolved.artists?.length;

    if (releaseId && (needsName || needsArtists)) {
        try {
            const fetched = await fetchReleaseInfoForTrackAutofill(releaseId);
            resolved = resolveReleaseInfoForTrackAutofill(fetched, resolved);
        } catch {
            // Keep merged form/atom values when the API call fails.
        }
    }

    return resolved;
}

/** Single, EP, or unset format → copy release title onto new tracks. Album → track titles differ. */
export function shouldAutofillTrackTitleFromRelease(type?: string): boolean {
    return (type || '').toUpperCase() !== 'ALBUM';
}

export function releaseMainArtistsForTrack(
    artists?: string[],
): API.MusicDistribution.Artist[] {
    return (artists || [])
        .filter((key): key is string => typeof key === 'string' && key.trim() !== '')
        .map((key_name) => ({key_name})) as API.MusicDistribution.Artist[];
}

export function buildNewTrackAutofill(releaseInfo: ReleaseInfoForTrackAutofill): {
    title: string;
    main_artists: API.MusicDistribution.Artist[];
} {
    const main_artists = releaseMainArtistsForTrack(releaseInfo.artists);
    const title =
        shouldAutofillTrackTitleFromRelease(releaseInfo.type) && releaseInfo.name
            ? releaseInfo.name
            : '';
    return {title, main_artists};
}
